/**
 * Vendor-portal direct-upload helpers for the three shop storage buckets.
 *
 * Deferred-upload contract
 * ------------------------
 * The vendor UI (apps/sell) does NOT upload bytes when the user picks a
 * file. The `<FileUpload>` component keeps the `File` in component state
 * (a `pending` slot with a local `URL.createObjectURL` preview). The
 * parent page's Save flow then runs three phases:
 *
 *   Phase 1 (upload): for every pending slot, call `uploadShopFile`
 *     which (a) requests a signed-upload URL from the
 *     `shop-storage-sign` Edge Function, (b) PUTs the bytes to that URL,
 *     and (c) returns the canonical public URL. `uploadPendingSlots`
 *     batches this and self-rolls-back if any single upload fails.
 *
 *   Phase 2 (persist): the parent calls the relevant
 *     create/updateShop* RPC with the final URL list (kept saved URLs +
 *     newly-uploaded URLs). If this fails, the parent best-effort
 *     deletes the just-uploaded objects so no orphan bytes are left.
 *
 *   Phase 3 (cleanup): for each URL that was previously saved but is no
 *     longer in the slot list, the parent calls `deleteShopFiles`. This
 *     is best-effort -- a rare network failure here leaks the bytes
 *     permanently because there is no orphan sweep.
 *
 * Net effect: there is no point in time where a storage object exists
 * without a corresponding parent row referencing it (other than the
 * Phase-3 best-effort delete failure case).
 *
 * The existing storage RLS (`shop_team_insert` / `shop_team_delete` on
 * `storage.objects`, declared in
 * `20260525000500_shop_storage_buckets.sql`) authorizes vendor team
 * members to insert and delete objects inside their own
 * `<vendor_id>/...` prefix. No new server code is needed for Phase 3.
 */

import { supabase } from '../client';
import type { FunctionsHttpError } from '@supabase/supabase-js';

export type ShopUploadBucket =
  | 'shop-vendor-assets'
  | 'shop-product-images'
  | 'shop-coa-docs';

export interface RequestShopUploadInput {
  bucket: ShopUploadBucket;
  vendorId: string;
  /** Original filename (used for the readable suffix on the storage path). */
  filename: string;
}

export interface ShopUploadGrant {
  /** Path inside the bucket, e.g. `<vendor_id>/<random>-<safe-filename>`. */
  path: string;
  /** One-shot signed upload URL (PUT). */
  signedUrl: string;
  /** Token to pass to `storage.uploadToSignedUrl`. */
  token: string;
  /** Final canonical public URL for the object once the PUT completes. */
  publicUrl: string;
}

interface SignFunctionResponse {
  ok: boolean;
  error?: string;
  signedUrl?: string;
  token?: string;
  path?: string;
  publicUrl?: string;
}

/**
 * Ask the edge broker for a signed-upload URL for `<vendorId>/<random>-<safe>`
 * inside the requested bucket. Does NOT upload the file.
 */
export async function requestShopUpload(
  input: RequestShopUploadInput,
): Promise<ShopUploadGrant> {
  const path = `${input.vendorId}/${buildSafeObjectName(input.filename)}`;
  const { data, error } = await supabase().functions.invoke<SignFunctionResponse>(
    'shop-storage-sign',
    { body: { bucket: input.bucket, path } },
  );
  if (error) throw error;
  if (!data || !data.ok || !data.signedUrl || !data.token || !data.publicUrl) {
    throw new Error(data?.error ?? 'shop-storage-sign returned no grant');
  }
  return {
    path: data.path ?? path,
    signedUrl: data.signedUrl,
    token: data.token,
    publicUrl: data.publicUrl,
  };
}

export interface UploadShopFileInput extends RequestShopUploadInput {
  file: File | Blob;
  /** Content type override; defaults to `file.type` (or octet-stream). */
  contentType?: string;
}

export interface UploadShopFileResult {
  publicUrl: string;
  path: string;
}

/**
 * One-shot helper: request a signed URL, upload the file, return the
 * permanent public URL. Throws on any failure.
 */
export async function uploadShopFile(
  input: UploadShopFileInput,
): Promise<UploadShopFileResult> {
  const grant = await requestShopUpload({
    bucket: input.bucket,
    vendorId: input.vendorId,
    filename: input.filename,
  });
  const contentType =
    input.contentType ||
    (input.file instanceof File ? input.file.type : '') ||
    'application/octet-stream';
  const { error } = await supabase()
    .storage.from(input.bucket)
    .uploadToSignedUrl(grant.path, grant.token, input.file, {
      contentType,
      upsert: false,
    });
  if (error) throw error;
  return { publicUrl: grant.publicUrl, path: grant.path };
}

// ---------------------------------------------------------------------------
// Phase 1 / Phase 3 helpers used by the save orchestration in parent pages.
// ---------------------------------------------------------------------------

export interface UploadPendingSlotsInput {
  bucket: ShopUploadBucket;
  vendorId: string;
  /** Files the user picked but hasn't saved yet, in display order. */
  files: readonly File[];
}

/**
 * Phase 1 helper. Uploads each File sequentially (to keep error
 * attribution simple) and returns the new public URLs in the same order.
 *
 * If any upload throws, the helper best-effort deletes the URLs that
 * already succeeded -- so a partial Phase 1 leaves zero bytes behind --
 * and re-throws the original error.
 *
 * Returns `[]` when `files` is empty (caller can no-op).
 */
export async function uploadPendingSlots(
  input: UploadPendingSlotsInput,
): Promise<string[]> {
  if (!input.files || input.files.length === 0) return [];
  const uploaded: string[] = [];
  try {
    for (const file of input.files) {
      const result = await uploadShopFile({
        bucket: input.bucket,
        vendorId: input.vendorId,
        filename: file.name,
        file,
      });
      uploaded.push(result.publicUrl);
    }
    return uploaded;
  } catch (err) {
    // Best-effort: clean up the bytes we did upload before re-throwing
    // so the caller never sees a "we uploaded 3 of 5 and threw" state.
    if (uploaded.length > 0) {
      await deleteShopFiles({ bucket: input.bucket, publicUrls: uploaded });
    }
    throw err;
  }
}

export interface DeleteShopFileInput {
  bucket: ShopUploadBucket;
  publicUrl: string;
}

export interface DeleteShopFilesInput {
  bucket: ShopUploadBucket;
  publicUrls: readonly string[];
}

/**
 * Phase 3 helper: remove a single storage object. Best-effort -- logs on
 * failure but never throws so a Phase-3 hiccup never breaks an
 * otherwise-successful Save.
 */
export async function deleteShopFile(
  input: DeleteShopFileInput,
): Promise<void> {
  await deleteShopFiles({ bucket: input.bucket, publicUrls: [input.publicUrl] });
}

/**
 * Phase 3 helper: remove a batch of storage objects via a single
 * `.remove()` call. Best-effort -- never throws. URLs that don't parse
 * as objects in `bucket` are skipped silently (they're either external
 * URLs the vendor pasted manually, or already-gone objects).
 */
export async function deleteShopFiles(
  input: DeleteShopFilesInput,
): Promise<void> {
  if (!input.publicUrls || input.publicUrls.length === 0) return;
  const paths: string[] = [];
  for (const url of input.publicUrls) {
    if (typeof url !== 'string' || url.length === 0) continue;
    const path = publicUrlToStoragePath(input.bucket, url);
    if (path) paths.push(path);
  }
  if (paths.length === 0) return;
  try {
    const { error } = await supabase().storage.from(input.bucket).remove(paths);
    if (error) {
      // Soft log; absent objects are a non-error for our use case so we
      // intentionally swallow `error.message`-class failures.
      console.warn(
        `[shopStorage.deleteShopFiles] bucket=${input.bucket} paths=${paths.length} message=${error.message}`,
      );
    }
  } catch (err) {
    console.warn(
      `[shopStorage.deleteShopFiles] bucket=${input.bucket} unexpected:`,
      err instanceof Error ? err.message : err,
    );
  }
}

/**
 * Parse a canonical public/sign URL back into the storage object path
 * inside `bucket`. Returns `null` if the URL doesn't reference an object
 * in this bucket (e.g. an externally-pasted URL the vendor typed in the
 * "paste URL" details box, or a different bucket's URL).
 *
 * Recognised shapes (Supabase Storage v1):
 *   `<origin>/storage/v1/object/public/<bucket>/<path>`
 *   `<origin>/storage/v1/object/sign/<bucket>/<path>?token=...`
 */
export function publicUrlToStoragePath(
  bucket: ShopUploadBucket,
  publicUrl: string,
): string | null {
  if (!publicUrl) return null;
  // Strip query string (sign URLs carry `?token=...`).
  const noQuery = publicUrl.split('?')[0] ?? publicUrl;
  // Find the bucket marker. We accept both `/public/<bucket>/` and
  // `/sign/<bucket>/` to be robust against future migrations between
  // public and signed download styles.
  const markers = [`/object/public/${bucket}/`, `/object/sign/${bucket}/`];
  for (const marker of markers) {
    const idx = noQuery.indexOf(marker);
    if (idx >= 0) {
      const path = noQuery.slice(idx + marker.length);
      // Defence: storage paths must be non-empty and shouldn't escape
      // the bucket with leading slashes.
      if (path.length === 0 || path.startsWith('/')) return null;
      try {
        return decodeURIComponent(path);
      } catch {
        return path;
      }
    }
  }
  return null;
}

/**
 * Build a storage-safe object name from a user-supplied filename, prefixed
 * with a random segment so concurrent uploads of the same name don't
 * collide. Strips path separators + control chars; preserves extension.
 */
function buildSafeObjectName(rawName: string): string {
  const trimmed = (rawName || 'file').replace(/^.*[\\/]/, '').trim();
  const dot = trimmed.lastIndexOf('.');
  const ext = dot > 0 ? trimmed.slice(dot).toLowerCase() : '';
  const stem = (dot > 0 ? trimmed.slice(0, dot) : trimmed)
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'file';
  const safeExt = ext.replace(/[^a-z0-9.]+/g, '').slice(0, 12);
  return `${randomId()}-${stem}${safeExt}`;
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    const uuid = crypto.randomUUID();
    return uuid.slice(0, uuid.indexOf('-') === -1 ? 8 : uuid.indexOf('-'));
  }
  return Math.random().toString(36).slice(2, 10);
}

// Re-export the supabase functions error type used by callers that want to
// branch on edge-fn HTTP errors specifically (not used yet but kept for
// parallelism with other services).
export type { FunctionsHttpError };
